/**
 * Удаление аккаунта.
 *
 * Отдельный экран, потому что это единственное необратимое действие в
 * приложении. Человек должен увидеть до нажатия ровно три вещи: что исчезнет,
 * что останется и что будет с деньгами.
 *
 * Три обещания, которые здесь исполняются вместе, — по отдельности каждое из
 * них становится ловушкой:
 *
 * · **Данные уходят сразу.** Ждать конца оплаченного периода не нужно: человек
 *   просил удалить свои сведения, а не отказаться от подписки.
 * · **Будущие списания прекращаются тем же действием.** «Отмените продление
 *   отдельно» после удаления аккаунта — дверь, которую уже нечем открыть.
 * · **Устройство очищается тоже.** Удаление на сервере при полной картине в
 *   браузере — это не удаление, а переезд данных в другое место.
 *
 * И одна честность: платёжной интеграции пока нет. Запрет на списания записан
 * у нас и подействует, когда провайдер появится, но выдавать это за
 * подтверждённую отмену привязки нельзя — списаний сейчас нет вовсе, потому
 * что оплата не принимается.
 */
import { useState } from 'react';
import { SheetWrap } from './sheet.js';
import { PasswordField } from './password-field.js';
import { deleteAccount, type DeleteResult, type User } from './sync.js';
import { resetEverything } from './store.js';

export function DeleteAccount({ user, onClose, onDone }: {
  user: User;
  onClose: () => void;
  /** Аккаунта больше нет: приложение возвращается к началу. */
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<DeleteResult | null>(null);

  if (done) {
    return (
      <SheetWrap title="Аккаунт удалён" onClose={onDone}>
        <div className="form">
          <div className="okline">
            Аккаунт {user.email} удалён, данные на этом устройстве стёрты.
          </div>
          <div className="note">{done.money}</div>
          <div className="note">
            Осталась одна запись: что удаление было, когда и по чьей просьбе —
            почта в ней хранится хешем. Она нужна, чтобы ответить, если ты
            обратишься снова, и чтобы отличить «удалили» от «потеряли».
          </div>
          <button className="btn pri" onClick={onDone}>Понятно</button>
        </div>
      </SheetWrap>
    );
  }

  return (
    <SheetWrap title="Удалить аккаунт" onClose={onClose}>
      <div className="form">
        <div className="warnline bad">
          Это необратимо. Счета, операции, цели, платежи, чеки, планы и
          разговоры со SKOKA будут удалены с сервера и с этого устройства.
        </div>

        <section className="blk">
          <div className="blkhead"><b>Что останется</b></div>
          <div className="li"><div className="nm">
            <b>Запись о самом удалении</b>
            <span>
              Время и хеш почты — чтобы ответить на повторное обращение и
              показать, что требование исполнено. Финансовых данных в ней нет.
            </span>
          </div></div>
        </section>

        <section className="blk">
          <div className="blkhead"><b>Резервные копии</b></div>
          <div className="li"><div className="nm">
            <b>Из копий данные уходят не в ту же секунду</b>
            <span>
              Сервер хранит копии до восьми недель — они нужны, чтобы твои
              деньги не пропали при сбое. Из живой базы данные удаляются сразу;
              из копий — вместе с истечением самой копии.
            </span>
          </div></div>
        </section>

        <section className="blk">
          <div className="blkhead"><b>Деньги и подписка</b></div>
          <div className="li"><div className="nm">
            <b>Ждать конца оплаченного периода не нужно</b>
            <span>
              Удаление происходит сразу, а запрет на будущие списания
              записывается этим же действием.
            </span>
          </div></div>
          <div className="note">
            Автопродление выключится, сохранённая карта отвяжется. Деньги за
            неиспользованные дни Pro удаление само не возвращает — верни их
            до удаления: «Подписка» → «Вернуть деньги». Записи об оплатах
            остаются без привязки к аккаунту: их хранить обязывает закон.
          </div>
        </section>

        <div className="li"><div className="nm">
          <b>{user.email}</b><span>этот аккаунт будет удалён</span>
        </div></div>
        <label>Пароль
          <PasswordField value={password} onChange={setPassword} autoComplete="current-password" /></label>
        <label className="check">
          <input type="checkbox" checked={confirm}
            onChange={(e) => setConfirm(e.target.checked)} />
          Понимаю, что данные не восстановить
        </label>

        {err && <div className="note warn">{err}</div>}

        <button className="btn danger" disabled={busy || !confirm || password.length === 0}
          onClick={async () => {
            setBusy(true); setErr(null);
            try {
              const result = await deleteAccount(password);
              // Сервер подтвердил — стираем устройство. Порядок именно такой:
              // стереть локальное раньше ответа значило бы потерять данные из-за
              // неверного пароля или обрыва сети.
              await resetEverything();
              setDone(result);
            } catch (e) {
              setErr(e instanceof Error ? e.message : 'Не удалось удалить');
              setBusy(false);
            }
          }}>
          {busy ? 'Удаляю…' : 'Удалить аккаунт и данные'}
        </button>
        <button className="btn" disabled={busy} onClick={onClose}>Не сейчас</button>
      </div>
    </SheetWrap>
  );
}
